import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Edit, Calendar, Users, Sparkles, ChevronDown, ChevronUp, FileText, Star, Footprints, Dumbbell, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_COLORS = {
  fuerza: { bg: 'bg-amber-900/40', text: 'text-amber-400', label: 'FUE', dot: 'bg-amber-400' },
  carrera: { bg: 'bg-blue-900/40', text: 'text-blue-400', label: 'CAR', dot: 'bg-blue-400' },
  bike: { bg: 'bg-orange-900/40', text: 'text-orange-400', label: 'BIK', dot: 'bg-orange-400' },
  funcional: { bg: 'bg-yellow-900/40', text: 'text-yellow-400', label: 'FUN', dot: 'bg-yellow-400' },
  excursion: { bg: 'bg-green-900/40', text: 'text-green-400', label: 'EXC', dot: 'bg-green-400' },
  natacion: { bg: 'bg-cyan-900/40', text: 'text-cyan-400', label: 'NAT', dot: 'bg-cyan-400' },
  especial: { bg: 'bg-pink-900/40', text: 'text-pink-400', label: 'ESP', dot: 'bg-pink-400' },
  descanso: { bg: 'bg-gray-800/40', text: 'text-gray-500', label: 'DES', dot: 'bg-gray-500' },
  evento: { bg: 'bg-gold/20', text: 'text-gold', label: 'EVT', dot: 'bg-gold' },
};

const DIAS = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];

export default function PlanesEntrenamiento() {
  const [planes, setPlanes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [planStats, setPlanStats] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showParseDialog, setShowParseDialog] = useState(false);
  const [parseText, setParseText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    nombre: '', descripcion: '', fecha_inicio: '', num_semanas: 1,
    cliente_id: '', evento: { nombre: '', fecha_inicio: '', fecha_fin: '' },
    recomendaciones: [], semanas: []
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadPlanes = useCallback(async () => {
    try {
      const [planesRes, statsRes] = await Promise.all([
        axios.get(`${API}/planes`, { headers }),
        axios.get(`${API}/planes/stats-all`, { headers })
      ]);
      setPlanes(planesRes.data);
      setPlanStats(statsRes.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadClientes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/client-plus/users`, { headers });
      setClientes(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadPlanes(); loadClientes(); }, [loadPlanes, loadClientes]);

  const resetForm = () => {
    setForm({
      nombre: '', descripcion: '', fecha_inicio: '', num_semanas: 1,
      cliente_id: '', evento: { nombre: '', fecha_inicio: '', fecha_fin: '' },
      recomendaciones: [], semanas: []
    });
  };

  const handleParse = async () => {
    if (!parseText.trim()) return;
    setParsing(true);
    try {
      const { data } = await axios.post(`${API}/planes/parse`, { texto: parseText }, { headers });
      if (data.error) {
        alert('Error al parsear: ' + data.error);
        return;
      }
      setForm(prev => ({
        ...prev,
        nombre: data.nombre || prev.nombre,
        descripcion: data.descripcion || prev.descripcion,
        fecha_inicio: data.fecha_inicio || prev.fecha_inicio,
        num_semanas: data.num_semanas || prev.num_semanas,
        evento: data.evento || prev.evento,
        recomendaciones: data.recomendaciones || prev.recomendaciones,
        semanas: data.semanas || prev.semanas,
      }));
      setShowParseDialog(false);
      setParseText('');
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setParsing(false); }
  };

  const handleSave = async () => {
    if (!form.nombre || !form.fecha_inicio) {
      alert('Nombre y fecha de inicio son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cliente_id: form.cliente_id || undefined,
        evento: form.evento?.nombre ? form.evento : null,
      };
      if (form.id) {
        await axios.put(`${API}/planes/${form.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/planes`, payload, { headers });
      }
      setShowCreate(false);
      resetForm();
      loadPlanes();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este plan?')) return;
    try {
      await axios.delete(`${API}/planes/${id}`, { headers });
      loadPlanes();
      setShowDetail(null);
    } catch (e) { alert('Error al eliminar'); }
  };

  const openEdit = (plan) => {
    setForm({ ...plan });
    setShowDetail(null);
    setShowCreate(true);
  };

  const [expandedWeek, setExpandedWeek] = useState(null);

  return (
    <>
    <Navbar />
    <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="planes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Planes de Entrenamiento</h1>
          <p className="text-gray-400 text-sm mt-1">Crea planes semanales para tus clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }}
          className="bg-gold hover:bg-gold/80 text-black font-medium"
          data-testid="btn-new-plan">
          <Plus className="h-4 w-4 mr-1" /> Nuevo
        </Button>
      </div>

      {/* Plans list */}
      {planes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Calendar className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-sm">No hay planes creados</p>
          <p className="text-xs mt-1">Crea tu primer plan de entrenamiento</p>
        </div>
      ) : (
        <div className="space-y-3">
          {planes.map(plan => (
            <button key={plan.id} onClick={() => setShowDetail(plan)}
              className="w-full text-left bg-gray-900 rounded-xl border border-gray-800 p-4 hover:border-gray-700 transition-colors"
              data-testid={`plan-${plan.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold truncate">{plan.nombre}</h3>
                  <p className="text-gray-400 text-xs mt-0.5">{plan.descripcion}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {plan.num_semanas} semanas
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" /> {plan.cliente_nombre}
                    </span>
                    <span>Inicio: {plan.fecha_inicio}</span>
                  </div>
                  {/* Stats de progreso */}
                  {planStats[plan.id] && planStats[plan.id].total_checkins > 0 && (
                    <div className="flex items-center gap-3 mt-2">
                      <span className="flex items-center gap-1 text-xs text-green-400 font-medium" data-testid={`stat-checkins-${plan.id}`}>
                        <CheckCircle className="h-3 w-3" /> {planStats[plan.id].total_checkins} entrenos
                      </span>
                      {planStats[plan.id].total_km > 0 && (
                        <span className="flex items-center gap-1 text-xs text-blue-400 font-medium" data-testid={`stat-km-${plan.id}`}>
                          <Footprints className="h-3 w-3" /> {planStats[plan.id].total_km} km
                        </span>
                      )}
                      {Object.entries(planStats[plan.id].by_type || {}).slice(0, 3).map(([tipo, count]) => {
                        const tc = TIPO_COLORS[tipo] || TIPO_COLORS.especial;
                        return (
                          <span key={tipo} className={`text-[10px] px-1.5 py-0.5 rounded ${tc.bg} ${tc.text}`}>
                            {tc.label} {count}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {plan.evento?.nombre && (
                  <span className="flex-shrink-0 ml-2 px-2 py-1 bg-gold/20 text-gold text-[10px] font-bold rounded-md">
                    <Star className="h-3 w-3 inline mr-0.5" />{plan.evento.nombre}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Quick parse button */}
            <Button type="button" variant="outline" className="w-full border-gold/50 text-gold hover:bg-gold/10"
              onClick={() => setShowParseDialog(true)} data-testid="btn-parse">
              <Sparkles className="h-4 w-4 mr-2" /> Importar con IA (pegar texto del plan)
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-gray-400 text-xs mb-1 block">NOMBRE DEL PLAN *</label>
                <Input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Plan Camino Santiago" className="bg-gray-800 border-gray-700 text-white" data-testid="input-nombre" />
              </div>
              <div className="col-span-2">
                <label className="text-gray-400 text-xs mb-1 block">DESCRIPCIÓN</label>
                <Input value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                  placeholder="Descripción corta" className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">FECHA INICIO *</label>
                <Input type="date" value={form.fecha_inicio} onChange={e => setForm(p => ({ ...p, fecha_inicio: e.target.value }))}
                  className="bg-gray-800 border-gray-700 text-white" data-testid="input-fecha" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">SEMANAS</label>
                <Input type="number" min={1} max={52} value={form.num_semanas}
                  onChange={e => setForm(p => ({ ...p, num_semanas: parseInt(e.target.value) || 1 }))}
                  className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="col-span-2">
                <label className="text-gray-400 text-xs mb-1 block">ASIGNAR A CLIENTE</label>
                <Select value={form.cliente_id} onValueChange={v => setForm(p => ({ ...p, cliente_id: v }))}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-cliente">
                    <SelectValue placeholder="Yo mismo (entrenador)" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="self" className="text-white">Yo mismo</SelectItem>
                    {clientes.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-white">{c.nombre} ({c.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Evento especial */}
            <div className="border border-gold/30 rounded-lg p-3">
              <p className="text-gold text-xs font-bold mb-2"><Star className="h-3 w-3 inline mr-1" />EVENTO ESPECIAL (Opcional)</p>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Nombre" value={form.evento?.nombre || ''} className="bg-gray-800 border-gray-700 text-white text-xs col-span-3"
                  onChange={e => setForm(p => ({ ...p, evento: { ...p.evento, nombre: e.target.value } }))} />
                <Input type="date" value={form.evento?.fecha_inicio || ''} className="bg-gray-800 border-gray-700 text-white text-xs"
                  onChange={e => setForm(p => ({ ...p, evento: { ...p.evento, fecha_inicio: e.target.value } }))} />
                <Input type="date" value={form.evento?.fecha_fin || ''} className="bg-gray-800 border-gray-700 text-white text-xs"
                  onChange={e => setForm(p => ({ ...p, evento: { ...p.evento, fecha_fin: e.target.value } }))} />
              </div>
            </div>

            {/* Weeks preview */}
            {form.semanas?.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs font-bold mb-2">SEMANAS ({form.semanas.length})</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {form.semanas.map((sem, si) => (
                    <div key={si} className="bg-gray-800 rounded-lg border border-gray-700">
                      <button onClick={() => setExpandedWeek(expandedWeek === si ? null : si)}
                        className="w-full flex items-center justify-between p-2 text-sm text-white">
                        <span className="font-medium">{sem.nombre || `Semana ${sem.numero}`}</span>
                        {expandedWeek === si ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {expandedWeek === si && (
                        <div className="px-2 pb-2 space-y-1">
                          {sem.dias?.map((dia, di) => (
                            <div key={di} className="flex items-start gap-2 text-xs">
                              <span className="text-gray-500 w-8 flex-shrink-0 font-mono">{DIAS[dia.dia]}</span>
                              <div className="flex flex-wrap gap-1">
                                {dia.actividades?.map((act, ai) => {
                                  const tc = TIPO_COLORS[act.tipo] || TIPO_COLORS.especial;
                                  return (
                                    <span key={ai} className={`${tc.bg} ${tc.text} px-1.5 py-0.5 rounded text-[10px]`}>
                                      {tc.label} {act.descripcion}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recomendaciones */}
            {form.recomendaciones?.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs font-bold mb-2">RECOMENDACIONES ({form.recomendaciones.length})</p>
                <div className="space-y-1">
                  {form.recomendaciones.map((r, i) => (
                    <div key={i} className="bg-gray-800 rounded px-2 py-1 text-xs">
                      <span className="text-gold font-medium">{r.titulo}</span>
                      <span className="text-gray-400"> — {r.texto}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-gray-700 text-gray-400">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}
                className="flex-1 bg-gold hover:bg-gold/80 text-black font-medium" data-testid="btn-save-plan">
                {saving ? 'Guardando...' : (form.id ? 'Actualizar' : 'Crear Plan')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Parse Dialog */}
      <Dialog open={showParseDialog} onOpenChange={setShowParseDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle><Sparkles className="h-5 w-5 inline mr-2 text-gold" />Importar Plan con IA</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-xs">Pega el texto del plan de entrenamiento y la IA lo convertirá en un plan estructurado automáticamente.</p>
          <textarea value={parseText} onChange={e => setParseText(e.target.value)}
            placeholder="Pega aquí el plan completo (semanas, días, ejercicios, recomendaciones...)"
            className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-gold"
            data-testid="textarea-parse" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowParseDialog(false)} className="flex-1 border-gray-700 text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleParse} disabled={parsing || !parseText.trim()}
              className="flex-1 bg-gold hover:bg-gold/80 text-black font-medium" data-testid="btn-parse-go">
              {parsing ? 'Analizando con IA...' : 'Analizar Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{showDetail.nombre}</DialogTitle>
              </DialogHeader>
              <p className="text-gray-400 text-sm">{showDetail.descripcion}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                <span><Calendar className="h-3 w-3 inline mr-1" />{showDetail.num_semanas} semanas</span>
                <span><Users className="h-3 w-3 inline mr-1" />{showDetail.cliente_nombre}</span>
                <span>Inicio: {showDetail.fecha_inicio}</span>
              </div>

              {showDetail.evento?.nombre && (
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 mt-3">
                  <p className="text-gold font-bold text-sm"><Star className="h-4 w-4 inline mr-1" />{showDetail.evento.nombre}</p>
                  <p className="text-gray-400 text-xs">{showDetail.evento.fecha_inicio} → {showDetail.evento.fecha_fin}</p>
                </div>
              )}

              {/* Progress stats */}
              {planStats[showDetail.id] && planStats[showDetail.id].total_checkins > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-3" data-testid="plan-detail-stats">
                  <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-3 text-center">
                    <CheckCircle className="h-5 w-5 text-green-400 mx-auto mb-1" />
                    <p className="text-green-400 text-xl font-black tabular-nums">{planStats[showDetail.id].total_checkins}</p>
                    <p className="text-gray-500 text-[10px] uppercase">Entrenos hechos</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-3 text-center">
                    <Footprints className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-blue-400 text-xl font-black tabular-nums">{planStats[showDetail.id].total_km}</p>
                    <p className="text-gray-500 text-[10px] uppercase">Km recorridos</p>
                  </div>
                  {Object.keys(planStats[showDetail.id].by_type || {}).length > 0 && (
                    <div className="col-span-2 bg-gray-800/60 border border-gray-700 rounded-xl p-3">
                      <p className="text-gray-400 text-[10px] font-bold uppercase mb-2">Desglose por tipo</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(planStats[showDetail.id].by_type).sort((a, b) => b[1] - a[1]).map(([tipo, count]) => {
                          const tc = TIPO_COLORS[tipo] || TIPO_COLORS.especial;
                          return (
                            <span key={tipo} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${tc.bg} ${tc.text}`}>
                              {tc.label} {count}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Weeks detail */}
              <div className="space-y-2 mt-3">
                {showDetail.semanas?.map((sem, si) => (
                  <div key={si} className="bg-gray-800 rounded-lg p-3">
                    <h4 className="text-white text-sm font-bold mb-2">{sem.nombre || `Semana ${sem.numero}`}</h4>
                    <div className="space-y-1.5">
                      {sem.dias?.map((dia, di) => (
                        <div key={di} className="flex items-start gap-2 text-xs">
                          <span className="text-gray-500 w-8 flex-shrink-0 font-mono font-bold">{DIAS[dia.dia]}</span>
                          <div className="flex flex-wrap gap-1">
                            {dia.actividades?.map((act, ai) => {
                              const tc = TIPO_COLORS[act.tipo] || TIPO_COLORS.especial;
                              return (
                                <span key={ai} className={`${tc.bg} ${tc.text} px-2 py-0.5 rounded text-[10px]`}>
                                  {tc.label} {act.descripcion}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recomendaciones */}
              {showDetail.recomendaciones?.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-white text-sm font-bold mb-2">Recomendaciones</h4>
                  <div className="space-y-2">
                    {showDetail.recomendaciones.map((r, i) => (
                      <div key={i} className="bg-gray-800 rounded-lg p-2">
                        <p className="text-gold text-xs font-bold">{r.titulo}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{r.texto}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => openEdit(showDetail)} className="flex-1 border-gray-700 text-gray-400">
                  <Edit className="h-4 w-4 mr-1" /> Editar
                </Button>
                <Button variant="outline" onClick={() => handleDelete(showDetail.id)}
                  className="flex-1 border-red-800 text-red-400 hover:bg-red-900/30">
                  <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
